import moment from "moment";
import { Card, Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Autocomplete,
    Box,
    Button,
    ControlLabelCheckBox,
    DatePicker,
    FormField,
    ImageUploader,
    QuillHtmlEditor,
    InputHtmlUploadEditor,
    SelectMultiLanguage,
    Typography,
    IPropDetailContainer,
    useCommonComponentContext,
} from "@maysoft/common-component-react";

import usePost from "./usePost";
import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import PostService from "../../services/message/Post.service";

import { IDataDetail, IDataError } from "./usePost";
import { Constants } from "../../constants/constants";
import { FileAccessMode, IsTrue, Mode, PostType } from "../../constants/enums";
import { GroupInputLinkVideo } from "../../components/Post/groupInputLinkVideo";



const PostEditContainer = (props: IPropDetailContainer & {
    extraLang?: string;
    onGetResourceCode: () => Promise<string>;
}) => {
    const Strings = useStrings();

    const {
        language,
        listMultiLanguage,

        onError,
        onSuccess,
        onHideLoading,
        onShowLoading,
    } = useCommonComponentContext();

    const {
        listPostTags,
        listPostTypes,

        getDataSubmit,
        getDetailPostById,
    } = usePost();

    const baseDataEditModelRef = useRef<IDataDetail>(undefined);

    const [isEdited, setIsEdited] = useState<boolean>(false);
    const [errorPost, setErrorPost] = useState<IDataError>({} as IDataError);
    const [dataPost, setDataPost] = useState<IDataDetail>({} as IDataDetail);
    const [selectedLanguage, setSelectedLanguage] = useState<string>(Constants.DefaultLanguage);

    const mode = useMemo(() => props.mode, [props.mode]);

    useEffect(() => {
        if (!Helpers.isNullOrEmpty(props?.idDetail)) {
            initPost(props?.idDetail, props?.extraLang);
        }
    }, [props?.idDetail]);

    useEffect(() => {
        setSelectedLanguage(language);
    }, [language]);

    const initPost = async (newId: string, extraLang?: string) => {
        onShowLoading();

        const dataEdit = await getDetailPostById(newId, extraLang);
        setDataPost(dataEdit);
        baseDataEditModelRef.current = dataEdit;

        onHideLoading();
    };

    const checkValidate = () => {
        let checked: boolean = true;
        let newErr: any = { ...errorPost };

        if (Helpers.isNullOrEmpty(dataPost?.type)) {
            checked = false;
            newErr["type"] = Strings.Common.REQUIRED;
        }
        if (Helpers.isNullOrEmpty(dataPost?.previewImage)) {
            checked = false;
            newErr["previewImage"] = Strings.Common.REQUIRED;
        }

        if (Helpers.isNullOrEmpty(dataPost?.title?.value?.[selectedLanguage])) {
            checked = false;
            newErr["title"] = Strings.Common.REQUIRED;
        }
        if (Helpers.isNullOrEmpty(dataPost?.content?.value?.[selectedLanguage])) {
            checked = false;
            newErr["content"] = Strings.Common.REQUIRED;
        }
        if (Helpers.isNullOrEmpty(dataPost?.shortDescription?.value?.[selectedLanguage])) {
            checked = false;
            newErr["shortDescription"] = Strings.Common.REQUIRED;
        }

        if (!checked) {
            setErrorPost(newErr);
        }

        return checked;
    };

    const handleSubmit = async () => {
        if (mode === Mode.View) {
            props.onChangeMode(Mode.Update);
        } else {
            if (!checkValidate()) {
                return;
            } else {
                try {
                    onShowLoading();
                    const newDataSubmit = await getDataSubmit(baseDataEditModelRef, dataPost);

                    if (!Helpers.isNullOrEmpty(dataPost?.id)) {
                        await PostService.update({ ...newDataSubmit, id: dataPost?.id });

                        setIsEdited(false);
                        props.onChangeMode(Mode.View);

                        await initPost(dataPost?.id, props?.extraLang);
                        onSuccess(Strings.POST.UPDATE_SUCCESS);
                    } else {
                        const resourceCode = await props.onGetResourceCode();
                        newDataSubmit.resourceCode = resourceCode;
                        await PostService.create(newDataSubmit);

                        setIsEdited(false);
                        onSuccess(Strings.POST.CREATE_SUCCESS);

                        props.onGoBack();
                    }
                } catch (error: any) {
                    const err = Helpers.renderExceptionError(error);
                    onError(err);
                } finally {
                    onHideLoading();
                }
            }
        }
    };

    const onValueChange = async (value: any, key: keyof IDataDetail) => {
        setDataPost((prev) => {
            let dataEdit: any = { ...prev };

            switch (key) {
                case "title":
                    dataEdit[key] = {
                        ...dataEdit[key],
                        value: {
                            ...dataEdit[key]?.value,
                            [selectedLanguage]: value,
                        },
                    };
                    break;
                case "shortDescription":
                    dataEdit[key] = {
                        ...dataEdit[key],
                        value: {
                            ...dataEdit[key]?.value,
                            [selectedLanguage]: value,
                        },
                    };
                    break;
                case "content":
                    dataEdit[key] = {
                        ...dataEdit[key],
                        value: {
                            ...dataEdit[key]?.value,
                            [selectedLanguage]: value,
                        },
                    };
                    break;
                case "eventTime":
                    if (!Helpers.isNullOrEmpty(value) && value?.isValid()) {
                        dataEdit[key] = value?.unix();
                    } else {
                        dataEdit[key] = null;
                    }
                    break;
                default:
                    dataEdit[key] = value;
                    break;
            }

            return dataEdit;
        });

        let newError = { ...errorPost, [key]: "" };
        setErrorPost(newError);

        setIsEdited(true);
    };

    const handleContentChange = useCallback((newContent: string) => {
        setDataPost(prev => ({
            ...prev,
            content: {
                ...prev.content,
                value: {
                    ...prev?.content?.value,
                    [selectedLanguage]: newContent,
                },
            }
        }))
    }, [selectedLanguage, dataPost?.content?.value?.[selectedLanguage]])

    const renderPostInfo = () => (
        <Card>
            <Box component={"form"} p={2}>
                <Box sx={{ mb: 1 }} display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between">
                    <Typography variant="h5">{Strings.POST.BASIC_INFO}</Typography>
                    <SelectMultiLanguage
                        defaultLanguage={selectedLanguage}
                        onChangeLanguage={(value) => {
                            const newData = {
                                ...dataPost,
                                title: Helpers.getDataMultiLanguage({
                                    data: dataPost.title,
                                    currentLanguage: language,
                                    listLanguage: listMultiLanguage,
                                }),
                                shortDescription: Helpers.getDataMultiLanguage({
                                    data: dataPost.shortDescription,
                                    currentLanguage: language,
                                    listLanguage: listMultiLanguage,
                                }),
                                content: Helpers.getDataMultiLanguage({
                                    data: dataPost.content,
                                    currentLanguage: language,
                                    listLanguage: listMultiLanguage,
                                }),
                            };

                            setDataPost(newData);
                            setSelectedLanguage(value);
                        }}
                    />
                </Box>

                <Grid container spacing={3} p={2}>
                    <Grid item xs={12} md={12}>
                        <ImageUploader
                            required
                            imageRatio="2:1"
                            label={Strings.POST.BANNER}
                            disabled={mode === Mode.View}
                            fileId={dataPost?.previewImage}
                            folderPath={Constants.FolderPath}
                            errorMessage={errorPost?.previewImage}
                            onChangeImage={(res) => {
                                onValueChange(res?.id, "previewImage");
                                onValueChange(res?.accessUrl, "previewImageUrl");
                            }}
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={3} p={2}>
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            required
                            mode={mode}
                            data={listPostTypes}
                            label={Strings.POST.TYPE}
                            defaultValue={dataPost?.type}
                            errorMessage={errorPost?.type}
                            placeholder={Strings.POST.SELECT_TYPE}
                            onChange={(value: any) => onValueChange(value, "type")}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ControlLabelCheckBox
                            mode={mode}
                            label={Strings.POST.IS_HIGHTLIGHTED}
                            value={dataPost?.isHighlighted === IsTrue.True}
                            onChangeValue={(value) => onValueChange(value ? IsTrue.True : IsTrue.False, "isHighlighted")}
                        />
                    </Grid>

                    {dataPost?.type !== PostType.News && (
                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                mode={mode}
                                data={listPostTags}
                                label={Strings.POST.CATEGORY}
                                defaultValue={dataPost?.postTag}
                                errorMessage={errorPost?.postTag}
                                placeholder={Strings.POST.SELECT_CATEGORY}
                                onChange={(value: any) => onValueChange(value, "postTag")}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                        <FormField
                            required
                            mode={mode}
                            maxLength={255}
                            label={Strings.POST.TITLE}
                            errorMessage={errorPost.title}
                            placeholder={Strings.POST.ENTER_TITLE}
                            defaultValue={dataPost?.title?.value?.[selectedLanguage]}
                            onBlur={(value: string) => onValueChange(value.substring(0, 255), "title")}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormField
                            required
                            mode={mode}
                            maxLength={255}
                            label={Strings.POST.DESCRIPTION}
                            errorMessage={errorPost.shortDescription}
                            placeholder={Strings.POST.ENTER_DESCRIPTION}
                            defaultValue={dataPost?.shortDescription?.value?.[selectedLanguage]}
                            onBlur={(value: string) => onValueChange(value, "shortDescription")}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <DatePicker
                            mode={mode}
                            value={
                                Helpers.isNullOrEmpty(dataPost?.eventTime) || dataPost?.eventTime === 0 || `${dataPost?.eventTime}` === "0"
                                    ? undefined
                                    : moment(dataPost?.eventTime * 1000)
                            }
                            label={Strings.POST.EVENT_TIME}
                            errorMessage={errorPost.eventTime}
                            placeholder={Strings.POST.SELECT_EVENT_TIME}
                            onChangeValue={(value: any) => onValueChange(value, "eventTime")}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        {/* <QuillHtmlEditor
                            required
                            mode={mode}
                            label={Strings.POST.CONTENT}
                            value={dataPost?.content?.value?.[selectedLanguage]}
                            errorMessage={errorPost?.content}
                            onChange={(textValue) => {
                                onValueChange(textValue, "content");
                            }}
                        /> */}
                        <InputHtmlUploadEditor
                            required
                            mode={mode}
                            label={Strings.POST.CONTENT}
                            fileAccessMode={FileAccessMode.Private}
                            defaultValue={dataPost?.content?.value?.[selectedLanguage]}
                            // onBlur={(textValue) => {
                            //     onValueChange(textValue, "content");
                            // }}
                            onBlur={handleContentChange}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <GroupInputLinkVideo
                            mode={mode}
                            postVideos={dataPost.postVideos || []}
                            onValueChange={(data) => {
                                onValueChange(data, "postVideos");
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
        </Card>
    );

    return (
        <Box mt={1}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "end" }}>
                        <Button
                            color={"secondary"}
                            onClick={() => {
                                if (isEdited) {
                                    Helpers.showConfirmAlert(Strings.Common.CANCEL_ACTION_MESSAGE, props.onGoBack);
                                } else {
                                    props.onGoBack();
                                }
                            }}
                        >
                            {Strings.Common.GO_BACK}
                        </Button>
                        <Button
                            sx={{ ml: 2 }}
                            color={"dark"}
                            onClick={() => {
                                handleSubmit();
                            }}
                        >
                            {mode === Mode.View ? Strings.Common.EDIT : Strings.Common.SAVE}
                        </Button>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    {renderPostInfo()}
                </Grid>
            </Grid>
        </Box>
    );
};

export default PostEditContainer;
